import { useState } from 'react';
import { Col, Tabs } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react';

import { useMeasurement } from '@/hooks/useMeasurement';
import { EnvironmentEnum } from '@/constants/enums';
import { useStores } from '@/hooks/use-stores';
import { EngineTab } from '@/constants';

import General from '../../General/General';
import { CentrifugalVersionContainer } from './styled';
import StationParametersTable from './StationParamatersTable';
import CompressorParametersTable from './CompressorParametersTable';
import SensorsKPITab from './SensorsKPIConfiguration/SensorsKPITab';
import { getAdvancedData, getBasicData, versionTabs } from './CentrifugalCompressors.utils';
import CentrifugalPublishedContainer from './CentrifugalPublishedContainer';
import CentrifugalDraftContainer from './CentrifugalDraftContainer';

const CentrifugalVersion = observer(
  ({
    activeTab,
    setActiveTab,
    isHistory,
  }: {
    activeTab: string;
    setActiveTab: (activeTab: EngineTab) => void;
    isHistory?: boolean;
  }) => {
    const [activeCentrifugalTab, setActiveCentrifugalTab] = useState(EngineTab.StationConfiguration);

    const [isSaveDisabled, setIsSaveDisabled] = useState(false);

    const { engineStore } = useStores();

    const { tunit } = useMeasurement();

    const unit = engineStore.unitMesurementSystem;

    const stationParameters = [engineStore.stationConfiguration?.stationParameter];

    const publishedDate = engineStore.publishedEngineConfig?.updatedDate;
    const draftPublishedDate = engineStore.draftEngineConfig?.updatedDate;
    const compressorParameters = engineStore.compressorsConfiguration?.compressorParameters;

    const onTabChange = (tab: EngineTab) => {
      setActiveCentrifugalTab(tab);
    };

    const basicData = getBasicData(stationParameters, tunit, unit);
    const advancedData = getAdvancedData(stationParameters, tunit, unit);

    const tabItems = [
      {
        label: t('stationParameters'),
        key: EngineTab.StationConfiguration,
        children: (
          <StationParametersTable
            activeTab={activeTab}
            data={basicData}
            advancedData={advancedData}
            setIsSaveDisabled={setIsSaveDisabled}
            showAdvanced
          />
        ),
      },
      {
        label: t('compressorParameters'),
        key: EngineTab.CompressorParameters,
        children: (
          <CompressorParametersTable
            activeTab={activeTab}
            setIsSaveDisabled={setIsSaveDisabled}
            compressorParameters={compressorParameters}
            isHistory={isHistory}
          />
        ),
      },
      {
        label: t('sensorsSelection'),
        key: EngineTab.SensorsConfiguration,
        children: <SensorsKPITab setIsSaveDisabled={setIsSaveDisabled} activeTab={activeTab} isPressureFallbackAllowed={true} isFlowFallbackAllowed={false} />,
      },

      ...(!engineStore.isReadOnly && activeTab === 'draft' && import.meta.env.VITE_ENV !== EnvironmentEnum.Production
        ? [
            {
              label: t('general'),
              key: EngineTab.General,
              children: <General setActiveTab={setActiveTab} />,
            },
          ]
        : []),
    ];

    return (
      <CentrifugalVersionContainer>
        {activeTab === versionTabs.published && (
          <CentrifugalPublishedContainer engineVersion={engineStore.engineVersion} publishedDate={publishedDate} />
        )}

        {activeTab === versionTabs.draft && (
          <CentrifugalDraftContainer
            engineVersion={engineStore.engineVersion}
            draftPublishedDate={draftPublishedDate}
            activeCentrifugalTab={activeCentrifugalTab}
            setIsSaveDisabled={setIsSaveDisabled}
            isSaveDisabled={isSaveDisabled}
            setActiveTab={setActiveTab}
            setActiveCentrifugalTab={setActiveCentrifugalTab}
          />
        )}
        <div className="centrifugal-tab-container">
          <Col xs={24} sm={24} span={24}>
            <Tabs
              activeKey={activeCentrifugalTab}
              onChange={onTabChange}
              animated={{
                inkBar: true,
                tabPane: false,
              }}
              items={tabItems}
              moreIcon={false}
            />
          </Col>
        </div>
      </CentrifugalVersionContainer>
    );
  },
);

export default CentrifugalVersion;
